import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { EqualMark, DotGrid, BarsMark } from "@/components/site/Marks";

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => {
      const number = e.detail?.number || "";
      const title = e.detail?.title || "";
      const ref = title ? `„${title}" (č. ${number})` : `č. ${number}`;
      const message =
        lang === "sk"
          ? `Dobrý deň, mám záujem o dielo ${ref}. Prosím o viac informácií.`
          : `Hello, I'm interested in artwork ${ref}. Please send me more information.`;
      setForm((f) => ({ ...f, message }));
    };
    window.addEventListener("prefill-contact", onPrefill);
    return () => window.removeEventListener("prefill-contact", onPrefill);
  }, [lang]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36 px-6 md:px-10">
      <DotGrid className="absolute right-[6%] top-28 opacity-25 hidden lg:block" color="#d97925" cols={6} rows={5} gap={11} />
      <BarsMark className="absolute left-[3%] bottom-24 opacity-20 hidden lg:block" color="#f5f2eb" count={4} w={30} />
      <div className="max-w-[1100px] mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <EqualMark className="opacity-55 mb-4" color="#d97925" w={44} />
          <p className="overline text-[#d97925] mb-5">{t("contact.overline")}</p>
          <h2 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl tracking-tight">
            {t("contact.title")}
          </h2>
          <p className="mt-8 text-[#a3a09a] text-lg leading-relaxed font-light max-w-md">{t("contact.intro")}</p>
          <p className="mt-10 font-serif-display italic text-2xl text-[#d97925]">{t("footer.tagline")}</p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          className="space-y-8"
          data-testid="contact-form"
        >
          <input
            data-testid="contact-name"
            required
            className="field"
            placeholder={t("contact.name")}
            value={form.name}
            onChange={set("name")}
          />
          <input
            data-testid="contact-email"
            required
            type="email"
            className="field"
            placeholder={t("contact.email")}
            value={form.email}
            onChange={set("email")}
          />
          <textarea
            data-testid="contact-message"
            required
            rows={4}
            className="field resize-none"
            placeholder={t("contact.message")}
            value={form.message}
            onChange={set("message")}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#f5f2eb] text-[#050505] py-4 overline rounded-full hover:bg-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? t("contact.sending") : t("contact.send")}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
